import ADIColors from "@NA/Utils/ADIColors";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    isWishListLineDiscontinued,
    isWishListLineRestricted,
} from "@insite/client-framework/Store/Data/WishListLines/WishListLinesSelectors";
import setWishListLineIsSelected from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/SetWishListLineIsSelected";
import updateQuantity from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateQuantity";
import updateUnitOfMeasure from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateUnitOfMeasure";
import updateWishListLineQuantities from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateWishListLineQuantities";
import translate from "@insite/client-framework/Translate";
import { ProductModel, WishListLineModel, WishListModel } from "@insite/client-framework/Types/ApiModels";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@NA/Overrides/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Check from "@insite/mobius/Icons/Check";
import RefreshCw from "@insite/mobius/Icons/RefreshCw";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import cloneDeep from "lodash/cloneDeep";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import ProductAvailability from "@NA/Overrides/Components/ProductAvailability";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import ADIProductDescription from "../RecentlyPurchasedProduct/ADIProductDescription";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getWishListsDataView } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { Cart } from "@insite/client-framework/Services/CartService";
import { HasCartLineContext } from "@insite/client-framework/Components/CartLineContext";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { useContext } from "react";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { isPriceAvailable } from "@NA/Utils/Utility";
import setUpdateCartLoading from "@NA/Handlers/ADIUpdateCartLoadingHandler";

interface OwnProps {
    wishList: WishListModel;
    wishListLine: WishListLineModel;
    productInfo: ProductInfo;
    onDeleteClick: (wishListLine: WishListLineModel) => void;
    onEditNotesClick: (wishListLine: WishListLineModel) => void;
    onMoveToCartClick: (wishListLine: WishListLineModel) => void;
    cart?: Cart;
    productId?: string;
    unitOfMeasure?: string;
}

const mapStateToProps = (state: CustomApplicationState, ownProps: OwnProps) => {
    const adiUserRoles = state.ADIGlobalData.ADIUserRoleStore;
    let wishlistId = "";
    const wishListsDataView = getWishListsDataView(state, undefined).value;
    const filteredWishList = wishListsDataView?.filter(item => item.properties.isSavedItemList === "1");
    if (filteredWishList && filteredWishList?.length > 0) {
        wishlistId = filteredWishList[0].id;
    }
    return {
        isSelected: !!state.pages.myListDetails.selectedWishListLineIds.find(o => o === ownProps.wishListLine.id),
        settingsCollection: getSettingsCollection(state),
        isQuantityUpdated: state.pages.myListDetails.wishListLinesWithUpdatedQuantity[ownProps.wishListLine.id],
        showADIAddToList: (adiUserRoles.fullAccess && adiUserRoles.accounting) || adiUserRoles.fullAccess,
        session: getSession(state),
        isCartLoading: getCurrentCartState(state).isLoading || state.ADIGlobalData.ADIUpdateCartLoading.loading,
        wishListSettings: getSettingsCollection(state).wishListSettings,
        wishlistId,
        filteredWishList,
    };
};

const mapDispatchToProps = {
    setWishListLineIsSelected,
    updateUnitOfMeasure,
    updateQuantity,
    updateWishListLineQuantities,
    setAddToListModalIsOpen,
    addToWishList,
    setUpdateCartLoading,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface MyListsDetailsProductListLineStyles {
    loadingOverlay?: LoadingSpinnerProps;
    editNotesLink?: LinkPresentationProps;
    lineInnerContainer?: GridContainerProps;
    selectedCheckboxHidden?: HiddenProps;
    imageGridItem?: GridItemProps;
    selectedCheckbox?: CheckboxPresentationProps;
    editNotesHidden?: HiddenProps;
    productImageStyles?: ProductImageStyles;
    descriptionGridItem?: GridItemProps;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    notesStyles?: SmallHeadingAndTextStyles;
    overflowMenuGridItem?: GridItemProps;
    partNumbersHidden?: HiddenProps;
    partNumbersGridItem?: GridItemProps;
    priceAndAvailabilityGridItem?: GridItemProps;
    nonReturnableContainer?: InjectableCss;
    priceAndAvailabilityInnerContainer?: GridContainerProps;
    priceGridItem?: GridItemProps;
    productPriceStyles?: ProductPriceStyles;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    uomAndQuantityGridItem?: GridItemProps;
    uomAndQuantityInnerContainer?: GridContainerProps;
    uomGridItem?: GridItemProps;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    quantityGridItem?: GridItemProps;
    productQuantityOrderedStyles?: InjectableCss;
    availabilityGridItem?: GridItemProps;
    productAvailabilityStyles?: ProductAvailabilityStyles;

    notesGridItem?: GridItemProps;
    buttonsGridItem?: GridItemProps;
    addToCartButton?: ButtonPresentationProps;
    deleteLinkHidden?: HiddenProps;
    deleteLink?: LinkPresentationProps;
    updateQuantityWrapper?: InjectableCss;
    updateQuantityLink?: LinkPresentationProps;
    quantityUpdatedIcon?: IconPresentationProps;
    quantityUpdatedText?: TypographyPresentationProps;
    restrictedDescriptionGridItem?: GridItemProps;
    restrictedDescriptionText?: TypographyPresentationProps;
    restrictedMessageText?: TypographyPresentationProps;
    restrictedRemoveItemLink?: LinkPresentationProps;
    wishListLineInfoGridItem?: GridItemProps;
    wishListLineInfoContainer?: GridContainerProps;
    productInfoGridItem?: GridItemProps;
    productInfoContainer?: GridContainerProps;
    productAvailability?: ProductAvailabilityStyles;
    rightLine?: InjectableCss;
    leftLine?: InjectableCss;
    productTotalPrice?: GridItemProps;
    productTotalPriceAvailability? :GridItemProps;
    VerificationText?:TypographyPresentationProps
}

export const productListLineStyles: MyListsDetailsProductListLineStyles = {
    loadingOverlay: {
        size: 22,
        css: css`
            position: inherit;
            width: 83.75px;
        `,
    },
    editNotesLink: {
        typographyProps: { size: 12 },
        css: css`
            margin-top: 5px;
        `,
    },
    lineInnerContainer: {
        gap: 20,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 1rem 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    css`
                        max-width: none;
                    `,
                    css`
                        max-width: 100%;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    selectedCheckboxHidden: { below: "md" },
    imageGridItem: {
        width: 2,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 10px;
                        flex-flow: column nowrap;
                        flex-basis: calc(28% - 6px);
                        max-width: calc(28% - 6px);
                        align-items: center;
                        > div {
                            margin-bottom: 7px;
                            margin-top: 10px;
                        }
                    `,
                    css`
                        font-size: 10px;
                        flex-flow: column nowrap;
                        flex-basis: calc(28% - 6px);
                        max-width: calc(28% - 6px);
                        align-items: center;
                        > div {
                            margin-bottom: 7px;
                            margin-top: 10px;
                        }
                    `,
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    null,
                ])}
        `,
    },
    selectedCheckbox: {
        sizeVariant: "small",
        css: css`
            margin-right: 10px;
        `,
    },

    editNotesHidden: { below: "md" },
    descriptionGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    overflowMenuGridItem: {
        width: [1, 1, 0, 0, 0],
    },
    partNumbersHidden: {
        below: "md",
        css: css`
            width: 100%;
        `,
    },
    partNumbersGridItem: { width: 12 },
    priceAndAvailabilityGridItem: {
        width: [5, 5, 5, 5, 5],
        css: css`
            flex-direction: column;
            align-items: center;
            flex-basis: 20.5% !important;
            padding: 0 15px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100% !important;
                        max-width: 100% !important;
                        > section {
                            align-items: flex-start !important;
                        }
                    `,
                    css`
                        flex-basis: 100% !important;
                        max-width: 100% !important;
                        > section {
                            align-items: flex-start !important;
                        }
                    `,
                    css``,
                    css``,
                    null,
                ])}
            > section {
                align-items: center;
                flex-basis: 20.5%;
            }
        `,
    },
    productTotalPrice: {
        css: css`
            flex-basis: 20%;
            max-width: 20%;
            font-size: 18px;
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css``,
                    css``,
                    null,
                ])}
        `,
    },
    productTotalPriceAvailability: {
        css: css`
            flex-basis: 20%;
            max-width: 20%;
            font-size: 14px;
            font-weight: bold;
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css``,
                    css``,
                    null,
                ])}
        `,
    },
    priceAndAvailabilityInnerContainer: {
        gap: 10,
        css: css`
            padding-top: 10px;

            > ${GridOffset} {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                        justify-content: flex-end;
                        align-items: flex-end;
                        `,
                        css`
                        justify-content: flex-end;
                        align-items: flex-end;
                        `,
                        css`
                        justify-content:flex-end;
                        `,
                        css`
                        justify-content: flex-end;
                        align-items: flex-end;
                        `,
                        css`
                        justify-content: flex-end;
                        align-items: flex-end;
                        `,                        
                    ])}
                    ${GridItemStyle}{
                    > section{
                     flex-direction: column;
                     >div:last-child{

                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                css`
                                justify-content: flex-end;
                                align-items: flex-end;
                                display:flex;
                                flex-direction: column;
                                `,
                                css`
                                justify-content: flex-end;
                                align-items: flex-end;
                                display:flex;
                                flex-direction: column;
                                `,
                                css`
                                justify-content:flex-end;
                                `,
                                css`
                                justify-content: flex-end;
                                align-items: flex-end;
                                display:flex;
                                flex-direction: column;
                                `,
                                css`
                                justify-content: flex-end;
                                align-items: flex-end;
                                display:flex;
                                flex-direction: column;
                                `,
                                
                            ])}
                            }
                            }
                    }

            }
            
        `,
    },
    priceGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            flex-direction: column;
            padding: 0 5px;
            [data-test-selector="productPrice_Wrapper"] {
                > section{
                 flex-direction: column;
                 }
                align-items: end;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        css`
                            justify-content: end;
                        `,
                        null,
                        null,
                    ])}
                    > span{
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                css`
                                justify-content: flex-end;
                                align-items: flex-end;
                                `,
                                css`
                                justify-content: flex-end;
                                align-items: flex-end;
                                `,
                                css`
                                justify-content:flex-end;
                                `,
                                css`
                                justify-content: flex-end;
                                align-items: flex-end;
                                `,
                                css`
                                justify-content: flex-end;
                                align-items: flex-end;
                                `,
                            ])}
        
                    }
            }
        `,
    },
    productPriceStyles: {       
        priceLabelText: {
            variant: "p",

            weight: 600,
            css: css`
                font-size: 16px;
                font-weight: 900;
                color: ${ADIColors.black};
            `,
        },
    },
    quantityBreakPricing: {
        viewLink: { typographyProps: { size: 12 } },
    },
    uomAndQuantityGridItem: {
        width: [12, 12, 12, 12, 7],
    },
    uomAndQuantityInnerContainer: {
        gap: 10,
        css: css``,
    },
    uomGridItem: { width: 9 },
    quantityGridItem: { width: 12 },
    productQuantityOrderedStyles: {
        css: css`
            font-size: 16px;
            font-weight: 900;
            color: ${ADIColors.black};
        `,
    },
    productUnitOfMeasureSelectStyles: {
        labelProps: {
            css: css``,
        },
        iconProps: {
            css: css``,
        },
    },
    availabilityGridItem: { width: 12 },
    buttonsGridItem: {
        css: css`
            display: flex;
            justify-content: end;
            width: 100%;
            max-width: 100%;
            white-space: nowrap;
            overflow: hidden;
            .disabledLinks {
                cursor: not-allowed;
                text-decoration: none !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        justify-content: flex-start;
                        padding: 0;
                    `,
                    css`
                        justify-content: flex-start;
                        padding: 0;
                    `,
                    css``,
                    css``,
                    null,
                ])}
        `,
    },

    notesGridItem: { width: 12 },
    addToCartButton: {
        css: css`
            width: 100%;
        `,
    },
    deleteLink: {
        css: css`
            margin-top: 20px;
            font-size: 14px;
            position: relative;
            ${TypographyStyle} {
                display: flex;
            }
            &:hover {
                ${TypographyStyle} {
                    text-decoration: underline;
                }
            }
            &:hover:disabled {
                ${TypographyStyle} {
                    text-decoration: none;
                }
            }
        `,
    },
    updateQuantityWrapper: {
        css: css`
            display: flex;
            margin-top: 10px;
        `,
    },
    updateQuantityLink: {
        typographyProps: { size: 12 },
        icon: {
            iconProps: {
                src: RefreshCw,
                size: 12,
            },
        },
    },
    quantityUpdatedIcon: {
        src: Check,
        size: 16,
        css: css`
            margin-right: 5px;
        `,
    },
    quantityUpdatedText: {
        size: 12,
    },
    restrictedDescriptionGridItem: {
        width: [12, 12, 6, 6, 5],
        css: css`
            flex-direction: column;
        `,
    },
    restrictedMessageText: { color: "warning" },
    nonReturnableContainer: {
        css: css`
            width: 100%;
            margin: 12px 0 0 0;
            font-size: 14px;
            color: ${getColor("text.accent")};
            font-stretch: normal;
            font-style: normal;
            line-height: 16px;
            letter-spacing: normal;
            font-weight: normal;
            padding-left: 6px;
            padding-bottom: 12px;
            font-family: "Nunito Sans", sans-serif;
        `,
    },
    wishListLineInfoGridItem: {
        width: [10, 10, 10, 10, 10],
        css: css`
            display: block;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 72% !important;
                        max-width: 72% !important;
                    `,
                    css`
                        flex-basis: 72% !important;
                        max-width: 72% !important;
                    `,
                    css``,
                    css``,
                    null,
                ])}
        `,
    },
    wishListLineInfoContainer: { gap: 20,

        css : css `
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                max-width:100%;
                `,
                null,
                null,
            ])}
        `,
     },
    productInfoGridItem: { width: [12, 12, 7, 7, 7] },
    productInfoContainer: {
        gap: 12,
        offsetCss: css`
            margin: auto;
        `,
    },
    leftLine: {
        css: css`
            position: relative;
            &::before {
                content: "|";
                color: ${getColor("text.accent")};
                width: 4px;
                height: 19px;
                margin: 0 5px;
                font-size: 14px;
                font-weight: normal;
            }
        `,
    },
    rightLine: {
        css: css`
            position: relative;
            &::after {
                content: "|";
                color: ${getColor("text.accent")};
                width: 4px;
                height: 19px;
                margin: 0 5px;
                font-size: 14px;
                font-weight: normal;
            }
        `,
    },
    productDescriptionStyles: {
        productDetailDescription: {
            css: css`
                color: ${getColor("text.link")};
                font-size: 18px;
                line-height: 22px;
                font-weight: 600;
                font-family: "Nunito Sans", sans-serif;
                max-width: 404px;
                word-break: break-word;
                &:hover {
                    color: ${getColor("text.link")};
                    text-decoration: underline;
                }
            `,
        },
        productDetailLink:{
            typographyProps: {
                
                css: css`                    
                color: #006ae3 !important;
                font-size: 18px;
                line-height: 22px;
                font-weight: 600;
                    &:hover {
                        color:#006ae3; !important;
                        text-decoration: underline;
                    }
                `,
            },
        },
    },
    VerificationText: {
        css: css`
                           
                                text-transform: none !important;
                                font-size: 14px !important;
                                font-weight: bold;
                                color: ${ADIColors.blue5} !important;                              
                            }
                        `,
    },
};


const styles = productListLineStyles;

const MyListsDetailsProductListLine: React.FC<Props> = ({
    addToWishList,
    setAddToListModalIsOpen,
    wishList,
    wishListLine,
    productInfo,
    settingsCollection,
    isQuantityUpdated,
    onDeleteClick,
    onMoveToCartClick,
    updateWishListLineQuantities,
    setWishListLineIsSelected,
    updateUnitOfMeasure,
    updateQuantity,
    showADIAddToList,
    session,
    isCartLoading,
    wishListSettings,
    setUpdateCartLoading,
}) => {
    const pricingIsAvailable = isPriceAvailable(
        wishListLine.pricing,
        wishListLine.qtyOrdered,
    );
    const toasterContext = useContext(ToasterContext);
    const product = mapWishListLineToProduct(wishListLine);

    const [isLoadingMoveToCart, setIsLoadingMoveToCart] = React.useState(false);
    const [isLoadingRemove, setIsLoadingRemove] = useState<boolean>(false);

    const handleMoveToCartClick = async () => {
        setIsLoadingMoveToCart(true);
        setUpdateCartLoading({ loading: true });
        try {
            await onMoveToCartClick(wishListLine);
        } finally {
            if (isCartLoading) {
                setIsLoadingMoveToCart(false);
            }
        }
    };
    const handleRemoveClick = async () => {
        setIsLoadingRemove(true);
        setUpdateCartLoading({ loading: true });

        try {
            await onDeleteClick(wishListLine);
        } finally {
            if (isCartLoading) {
                setIsLoadingRemove(false);
            }
        }
    };

    const onUpdateQuantityClick = () => {
        updateWishListLineQuantities({ reloadWishListLines: true });
    };

    const canEditWishList = wishList.allowEdit || !wishList.isSharedList;

    if (isWishListLineRestricted(wishListLine, productInfo) || isWishListLineDiscontinued(wishListLine, productInfo)) {
        return (
            <GridContainer
                {...styles.lineInnerContainer}
                data-test-selector={`${wishListLine.productId}_${wishListLine.selectedUnitOfMeasure}`}
            >
                <GridItem {...styles.restrictedDescriptionGridItem}>
                    <Typography {...styles.restrictedDescriptionText}>{wishListLine.shortDescription}</Typography>
                    <Hidden {...styles.partNumbersHidden}>
                        <ProductPartNumbers
                            productNumber={product.productNumber}
                            customerProductNumber={product.customerProductNumber}
                            manufacturerItem={product.manufacturerItem}
                            extendedStyles={styles.productPartNumbersStyles}
                        />
                    </Hidden>
                </GridItem>
                <GridItem {...styles.partNumbersGridItem}>
                    <ProductPartNumbers
                        productNumber={product.productNumber}
                        customerProductNumber={product.customerProductNumber}
                        manufacturerItem={product.manufacturerItem}
                        extendedStyles={styles.productPartNumbersStyles}
                    />
                </GridItem>
                <GridItem {...styles.priceAndAvailabilityGridItem}>
                    <Typography {...styles.restrictedMessageText} data-test-selector="restrictedMessage">
                        {isWishListLineRestricted(wishListLine, productInfo) && (
                            <>{siteMessage("Lists_Item_Not_Displayed_Due_To_Restrictions")}</>
                        )}
                        {isWishListLineDiscontinued(wishListLine, productInfo) && (
                            <>{siteMessage("Lists_Item_Not_Displayed_Due_To_Discontinued")}</>
                        )}
                    </Typography>
                </GridItem>
                <GridItem {...styles.buttonsGridItem}>
                    {canEditWishList && (
                        <Link
                            {...styles.restrictedRemoveItemLink}
                            onClick={() => onDeleteClick(wishListLine)}
                            data-test-selector="removeRestricted"
                        >
                            {translate("Remove item")}
                        </Link>
                    )}
                </GridItem>
            </GridContainer>
        );
    }

    const { productSettings } = settingsCollection;
    const productContext: ProductContextModel = {
        product,
        productInfo,
        onQtyOrderedChanged: qtyOrdered => {
            updateQuantity({ wishListLineId: wishListLine.id, qtyOrdered });
        },
        onUnitOfMeasureChanged: unitOfMeasure => {
            updateUnitOfMeasure({ wishListLineId: wishListLine.id, unitOfMeasure });
        },
    };
    const availabilityMessage = wishListLine.availability;
    //const availabilityMessage = wishListLine.pricing && wishListLine.pricing.additionalResults;

    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }
        const productInfo = {
            productId: wishListLine.productId,
            qtyOrdered: wishListLine.qtyOrdered,
            unitOfMeasure: wishListLine.unitOfMeasure,
        };
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    return (
        <ProductContext.Provider value={productContext}>
            <GridContainer
                {...styles.lineInnerContainer}
                data-test-selector={`${wishListLine.productId}_${wishListLine.selectedUnitOfMeasure}`}
            >
                <GridItem {...styles.imageGridItem}>
                    <ProductImage product={productContext} extendedStyles={styles.productImageStyles} />
                    <Hidden above="xs">
                        <GridItem {...styles.quantityGridItem}>
                            <Typography {...styles.productQuantityOrderedStyles}>
                                {translate(`Qty: ${wishListLine.qtyOrdered}`)}
                            </Typography>
                        </GridItem>
                    </Hidden>
                </GridItem>
                <GridItem {...styles.wishListLineInfoGridItem}>
                    <GridContainer {...styles.wishListLineInfoContainer}>
                        <GridItem {...styles.productInfoGridItem}>
                            <GridContainer {...styles.productInfoContainer}>
                                <GridItem {...styles.descriptionGridItem}>
                                    <ADIProductDescription
                                        sourceName="My Lists"
                                        session={session}
                                        product={product}
                                        extendedStyles={styles.productDescriptionStyles}
                                    />
                                </GridItem>
                                <GridItem {...styles.partNumbersGridItem}>
                                    <ProductPartNumbers
                                        showLabel={true}
                                        productNumber={product.productNumber}
                                        customerProductNumber={product.customerProductNumber}
                                        manufacturerItem={product.manufacturerItem}
                                        extendedStyles={styles.productPartNumbersStyles}
                                    />
                                </GridItem>
                                {wishListLine.properties["abcCode"] === "N" && (
                                    <StyledWrapper {...styles.nonReturnableContainer}>
                                        {translate("Non-Returnable")}
                                    </StyledWrapper>
                                )}
                                {availabilityMessage && (
                                    <GridItem {...styles.availabilityGridItem}>
                                        <ProductAvailability
                                            product={wishListLine}
                                            productId={wishListLine.productId!}
                                            availability={{
                                                message: availabilityMessage.message,
                                                messageType: Number(availabilityMessage?.messageType),
                                                requiresRealTimeInventory: false,
                                            }}
                                            unitOfMeasure={wishListLine.unitOfMeasure}
                                            trackInventory={wishListLine.trackInventory}
                                            failedToLoadInventory={false}
                                            extendedStyles={styles.productAvailability}
                                        />
                                    </GridItem>
                                )}
                            </GridContainer>
                        </GridItem>
                        <GridItem {...styles.priceAndAvailabilityGridItem}>
                            <GridContainer
                                {...styles.priceAndAvailabilityInnerContainer}
                                data-test-selector="priceAndAvailabilityInnerContainer"
                            >
                                <Hidden below="md">
                                    <GridItem {...styles.quantityGridItem}>
                                        <Typography {...styles.productQuantityOrderedStyles}>
                                            {translate(`Qty: ${wishListLine.qtyOrdered}`)}
                                        </Typography>
                                    </GridItem>
                                </Hidden>
                                <GridItem {...styles.priceGridItem}>
                                    <ProductPrice
                                        product={productContext}
                                        showSavings={true}
                                        showSavingsAmount={productSettings.showSavingsAmount}
                                        showSavingsPercent={productSettings.showSavingsPercent}
                                        extendedStyles={styles.productPriceStyles}
                                    />
                                    <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                                </GridItem>
                                <Hidden above="sm">
                                    <GridItem {...styles.priceGridItem} data-test-selector="unitPrice">
                                        <Typography>
                                            {translate("(Unit Price:")}&nbsp;{product.pricing?.actualPriceDisplay}
                                            {translate(")")}
                                        </Typography>
                                    </GridItem>
                                </Hidden>
                                {/* {productSettings.showInventoryAvailability && availabilityMessage && (
                                    <GridItem {...styles.availabilityGridItem}>
                                        <ProductAvailability
                                            productId={wishListLine.productId!}
                                            availability={{
                                                message: availabilityMessage.message,
                                                messageType: parseInt(availabilityMessage.messageType),
                                                requiresRealTimeInventory: false,
                                            }}
                                            unitOfMeasure={wishListLine.unitOfMeasure}
                                            trackInventory={wishListLine.trackInventory}
                                            failedToLoadInventory={false}
                                            extendedStyles={styles.productAvailability}
                                        />
                                    </GridItem>
                                )} */}
                            </GridContainer>
                        </GridItem>
                        {pricingIsAvailable && (
                        <GridItem {...styles.productTotalPrice}>
                            <Typography {...styles.productQuantityOrderedStyles}>
                                {wishListLine.pricing?.extendedUnitNetPriceDisplay}
                            </Typography>
                        </GridItem>
                        )}
                        {!pricingIsAvailable && (
                        <GridItem {...styles.productTotalPriceAvailability}>                            
                            {/* <Typography>{translate("Pricing Currently Not Available")}</Typography> */}
                                {localStorage.getItem("CreditStatus") !== "O" && localStorage.getItem("CreditStatus") !== "" && localStorage.getItem("CreditStatus") !== null ? <Typography {...styles.VerificationText} as="span">{translate("Account Pending Verification")}</Typography> : <Typography>{translate("Pricing Currently Not Available")}</Typography>}
                        </GridItem>
                        )}
                    </GridContainer>
                    <GridItem {...styles.buttonsGridItem}>
                        <>
                            <Link
                                {...styles.deleteLink}
                                onClick={handleRemoveClick}
                                data-test-selector="deleteListItem"
                                disabled={isLoadingRemove || isCartLoading}
                                className={`${isLoadingRemove || isCartLoading ? "disabledLinks" : ""}`}
                            >
                                {isLoadingRemove ? (
                                    <LoadingSpinner {...styles.loadingOverlay}></LoadingSpinner>
                                ) : (
                                    translate("Remove")
                                )}
                            </Link>
                            <Link
                                {...styles.deleteLink}
                                onClick={handleMoveToCartClick}
                                data-test-selector="movetocart"
                                className={`${isLoadingMoveToCart || isCartLoading ? "disabledLinks" : ""}`}
                                disabled={isLoadingMoveToCart || isCartLoading}
                            >
                                <Typography {...styles.leftLine}></Typography>

                                {isLoadingMoveToCart ? (
                                    <LoadingSpinner {...styles.loadingOverlay}></LoadingSpinner>
                                ) : (
                                    translate("Move To Cart")
                                )}
                                <Typography {...styles.rightLine}></Typography>
                            </Link>
                            {showADIAddToList && (
                                <Link
                                    {...styles.deleteLink}
                                    onClick={() => addToListClickHandler()}
                                    data-test-selector="movetocart"
                                    disabled={isCartLoading}
                                    className={`${isCartLoading ? "disabledLinks" : ""}`}
                                >
                                    {translate("Add To List")}
                                </Link>
                            )}
                        </>
                    </GridItem>
                </GridItem>
            </GridContainer>
        </ProductContext.Provider>
    );
};

const mapWishListLineToProduct = (wishListLine: WishListLineModel) => {
    const product = cloneDeep(wishListLine) as any as ProductModel;
    product.id = wishListLine.productId;
    product.productNumber = wishListLine.erpNumber;
    product.productTitle = wishListLine.shortDescription;
    product.unitOfMeasures =
        wishListLine?.productUnitOfMeasures?.map(u => ({
            id: u.productUnitOfMeasureId,
            ...u,
        })) || null;
    return product;
};

export default connect(mapStateToProps, mapDispatchToProps)(MyListsDetailsProductListLine);
